/**
 * Calendar blocks API — blocked time & holidays (11E.7).
 *
 * Blocks are overlays: they render behind session chips and never suppress
 * or cancel a session. See the backend service docstring for why that
 * boundary exists (a mistaken holiday must not destroy schedule).
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { api } from '@core/api/client';

/**
 * Hand-declared rather than generated — see the note in
 * `@features/calendar/api/sessions.ts`: `shared/api-types/index.ts` cannot
 * be regenerated in isolation today (PRE-REQ-C). Mirror any backend change
 * to `CalendarBlock*` in `backend/app/features/calendar/schemas.py` here.
 */
export type CalendarBlockKind = 'blocked_time' | 'holiday';

export interface CalendarBlockResponse {
  id: string;
  kind: CalendarBlockKind;
  title: string;
  starts_at: string;
  ends_at: string;
  all_day: boolean;
  teacher_id: string | null;
  room_id: string | null;
  branch_id: string | null;
  notes: string | null;
  created_at: string;
  updated_at: string;
}

export interface CalendarBlockCreate {
  kind: CalendarBlockKind;
  title: string;
  starts_at: string;
  ends_at: string;
  all_day?: boolean;
  teacher_id?: string | null;
  room_id?: string | null;
  branch_id?: string | null;
  notes?: string | null;
}

export type CalendarBlockUpdate = Partial<Omit<CalendarBlockCreate, 'kind'>>;

export interface ListBlocksParams {
  from: string; // ISO datetime
  to: string;
  kind?: CalendarBlockKind;
  teacher_id?: string;
}

export const blockKeys = {
  all: ['calendar', 'blocks'] as const,
  list: (p: ListBlocksParams) => ['calendar', 'blocks', 'list', p] as const,
};

export async function listBlocks(params: ListBlocksParams): Promise<CalendarBlockResponse[]> {
  return api.get<CalendarBlockResponse[]>('/v1/calendar/blocks', {
    query: {
      from: params.from,
      to: params.to,
      kind: params.kind || undefined,
      teacher_id: params.teacher_id || undefined,
    },
  });
}

export async function createBlock(body: CalendarBlockCreate): Promise<CalendarBlockResponse> {
  return api.post<CalendarBlockResponse>('/v1/calendar/blocks', body);
}

export async function updateBlock(
  id: string,
  body: CalendarBlockUpdate,
): Promise<CalendarBlockResponse> {
  return api.patch<CalendarBlockResponse>(`/v1/calendar/blocks/${id}`, body);
}

export async function deleteBlock(id: string): Promise<void> {
  return api.delete<void>(`/v1/calendar/blocks/${id}`);
}

export function useBlocks(params: ListBlocksParams) {
  return useQuery({
    queryKey: blockKeys.list(params),
    queryFn: () => listBlocks(params),
    staleTime: 30_000,
  });
}

export function useCreateBlock() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: CalendarBlockCreate) => createBlock(body),
    onSuccess: () => qc.invalidateQueries({ queryKey: blockKeys.all }),
  });
}

export function useUpdateBlock() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: CalendarBlockUpdate }) => updateBlock(id, body),
    onSuccess: () => qc.invalidateQueries({ queryKey: blockKeys.all }),
  });
}

export function useDeleteBlock() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => deleteBlock(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: blockKeys.all }),
  });
}
